import { t } from "@utils/i18n";

interface HideableItem {
  hidden?: boolean;
}

/** Splits a list of dataset elements (variables, values, questions) into visible and hidden. */
export function partitionByVisibility<T extends HideableItem>(
  items: T[],
): { visible: T[]; hidden: T[] } {
  const visible: T[] = [];
  const hidden: T[] = [];
  for (const item of items) {
    (item.hidden ? hidden : visible).push(item);
  }
  return { visible, hidden };
}

/**
 * Visible items, plus the currently selected item even if it is hidden —
 * lets a saved focus/condition keep its hidden source selected while new
 * choices stay restricted to visible sources.
 */
export function filterVisiblePreservingSelected<T extends HideableItem>(
  items: T[],
  isSelected: (item: T) => boolean,
): T[] {
  return items.filter((item) => !item.hidden || isSelected(item));
}

/** Map of id -> "Hidden in dataset" for items that are hidden, for HPDropdown's getOptionDisabled/subtitle. */
export function buildHiddenReasonMap<T extends HideableItem>(
  items: T[],
  getId: (item: T) => string,
  reason: string = t("common.hiddenInDataset"),
): Map<string, string> {
  const reasons = new Map<string, string>();
  for (const item of items) {
    if (item.hidden) {
      reasons.set(getId(item), reason);
    }
  }
  return reasons;
}

interface LabeledHideableItem extends HideableItem {
  label?: string;
  title?: string;
  rowtitle?: string;
  coltitle?: string;
  value?: string | number;
}

/**
 * Map of label-variant -> "Hidden in dataset" for items that are hidden.
 * Bar Chart's fetched response data (getDatasetQuestionsResponse) carries a
 * sequential per-fetch index as its "variableId" — not the real
 * datasetElementId from the /variables universe — so a hidden bar can only
 * be identified by matching its display label against the universe's label
 * variants, the same reconciliation Grouped/Stacked Bar already do by id
 * (see grouped-bar-utils.ts's buildLabelMatcher).
 */
export function buildHiddenLabelReasonMap(
  items: LabeledHideableItem[],
  reason: string = t("common.hiddenInDataset"),
): Map<string, string> {
  const reasons = new Map<string, string>();
  for (const item of items) {
    if (!item.hidden) continue;
    const variants = [
      item.label,
      item.title,
      item.rowtitle,
      item.coltitle,
      item.value != null ? String(item.value) : undefined,
    ].filter((v): v is string => !!v);
    for (const variant of variants) {
      if (!reasons.has(variant)) reasons.set(variant, reason);
    }
  }
  return reasons;
}
